import { useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAudio } from '../../contexts/AudioContext';
import { Sidebar } from './Sidebar';
import { MobileTabBar } from './MobileTabBar';
import { MobileHeader } from './MobileHeader';
import { AudioPlayer } from '../AudioPlayer';
import { Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { syncStatusBar, initKeyboard, isDarkTheme } from '../../lib/nativeUi';
import { registerPush } from '../../lib/pushNotificationService';

export const DashboardLayout = () => {
  const { user, loading, isApproved, isAdmin } = useAuth();
  const { currentTrack, nextTrack, previousTrack, closePlayer, hasNext, hasPrevious } = useAudio();

  // Configure la barre d'état Android/iOS au chargement selon le thème, et
  // initialise le comportement du clavier natif. No-op sur le web.
  useEffect(() => {
    syncStatusBar(isDarkTheme());
    initKeyboard();
    const observer = new MutationObserver(() => syncStatusBar(isDarkTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Enregistrement des notifications push — uniquement sur natif (Android/iOS),
  // où la demande de permission fait partie de l'expérience attendue. Sur le
  // web, l'activation reste manuelle (bouton dans les Paramètres).
  useEffect(() => {
    if (user?.uid && Capacitor.isNativePlatform()) {
      registerPush(user.uid);
    }
  }, [user?.uid]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Rediriger tous les membres qui ne sont pas validés (en attente ou rejetés)
  // Sauf si c'est l'admin par défaut
  if (!isApproved && !isAdmin) {
    return <Navigate to="/pending-approval" replace />;
  }

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col md:block">
      {/* En-tête mobile compact : branding (retour accueil) + notifications. */}
      <MobileHeader />

      {/* Sidebar : masquée sur mobile, affichée à partir de md. */}
      <div className="hidden md:block">
        <Sidebar isOpen={false} onClose={() => {}} />
      </div>

      <main className="flex-1 w-full transition-all duration-300 md:pl-64">
        {/* pb-tabbar : réserve l'espace de la barre d'onglets sur mobile.
            Lecteur audio : marge additionnelle quand un titre joue. */}
        <div
          className={`px-4 pt-3 md:p-8 max-w-full overflow-x-hidden pb-tabbar md:pb-8 ${
            currentTrack ? 'mb-24 md:mb-20' : ''
          }`}
        >
          <Outlet />
        </div>
      </main>

      {/* Barre de navigation basse (mobile uniquement) */}
      <MobileTabBar />

      {currentTrack && (
        <AudioPlayer
          track={currentTrack}
          onClose={closePlayer}
          onNext={hasNext ? nextTrack : null}
          onPrevious={hasPrevious ? previousTrack : null}
        />
      )}
    </div>
  );
};
